import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppContext } from "../../../../AppContext";
import { useCallback, useEffect, useMemo, useState } from "react";
import { dateUtility } from "../../../../common/infrastructure/utilities/dateUtility";
import { IPlannerDayModel } from "../models/plannerDayModel";
import { IPlannerEventModel } from "../models/plannerEventModel";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import {
  constructAddEventHref,
  constructPlannerEventHref,
} from "../../../../common/infrastructure/routes/routePaths";
import { showCustomConfirmDialog } from "../../../../common/components/dialogs/AppConfirmDialog";
import { useAddAdditionalLayoutButton } from "../../../../common/infrastructure/hooks/useAddAdditionalLayoutButton";
import { useReservationSearch } from "./useReservationSearch";
import { IconType } from "../../../../common/icons/Icon";
import { ISelectFieldModel } from "../../../../common/components/forms/inputs/dropdown/models/selectFieldModel";
import { getCurrentUserRole } from "../../../../common/infrastructure/helpers/userHelper";
import { UserRole } from "../../../../common/enums";
import {
  ALL_HALLS_OPTION_ID,
  IPlannerFiltersState,
  createDefaultFiltersState,
  isAllHallsSelected,
} from "../models/plannerFilters";
import {
  loadFiltersFromStorage,
  saveFiltersToStorage,
  updateInstructorLabels,
  filterEventsByFilters,
} from "../utils/plannerFilterUtils";
import { createTileContentFunction } from "../utils/plannerCalendarUtils";
import { usePlannerPanelExpand } from "./usePlannerPanelExpand";
import { useHandoverNote } from "./useHandoverNote";
import { usePlannerEventClick } from "./usePlannerEventClick";
import {
  createCacheKey,
  shouldUpdateCache,
  updateEventsCache,
  updateCacheTimestamps,
  getAllEventsFromCache,
} from "../utils/plannerCacheUtils";

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

export const usePlanner = () => {
  // Reservation search hook
  const reservationSearch = useReservationSearch();
  // Stacked-layout only: slides the schedule panel over the calendar.
  const panelExpand = usePlannerPanelExpand();
  const { selectedStudioId, selectedStudio, currentUser } = useAppContext();
  const tenantUsesMoreThanOneHall =
    selectedStudio?.tenantUsesMoreThanOneHall ?? false;
  const { mutate } = useApiMutation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlDate = searchParams.get("date");
  const urlEventId = searchParams.get("eventId");
  const urlSelectedStudio = searchParams.get("selectedStudio");

  const [calendarViewDate, setCalendarViewDate] = useState(() => {
    return urlDate ? dateUtility.parseDateForPlanner(urlDate) : new Date();
  });
  const [selectedValue, setSelectedValue] = useState<Date>(() => {
    return urlDate ? dateUtility.parseDateForPlanner(urlDate) : new Date();
  });
  // Per-day hand-over thread for the day the calendar currently shows.
  const handover = useHandoverNote({ selectedDate: selectedValue });

  const [eventsCache, setEventsCache] = useState<
    Map<string, IPlannerDayModel[]>
  >(new Map());

  const [cacheTimestamps, setCacheTimestamps] = useState<Map<string, number>>(
    new Map()
  );

  const [filters, setFilters] = useState<IPlannerFiltersState>(() => {
    const initialFilters = createDefaultFiltersState();
    const storedFilters = loadFiltersFromStorage();
    return { ...initialFilters, ...storedFilters };
  });

  // Filters sidebar state
  const [isFiltersSidebarOpen, setIsFiltersSidebarOpen] = useState(false);

  // Bulk instructor assignment dialog state
  const [isBulkAssignInstructorDialogOpen, setIsBulkAssignInstructorDialogOpen] =
    useState(false);

  // Bulk delete dialog state
  const [isBulkDeleteEventsDialogOpen, setIsBulkDeleteEventsDialogOpen] =
    useState(false);

  const currentCacheKey = createCacheKey(
    calendarViewDate.getFullYear(),
    calendarViewDate.getMonth() + 1
  );

  const navigate = useNavigate();

  // Deep-link support: a shared link like /calendar?eventId=N opens the class for
  // every role. For staff with planner access the calendar renders this Planner, so
  // route them to the planner event view (which also shows classes hidden from the
  // portal). We wait until the link's pinned studio (if any) is the active one so the
  // event page loads in the correct studio context.
  useEffect(() => {
    if (!urlEventId) {
      return;
    }

    const eventId = Number(urlEventId);
    if (Number.isNaN(eventId) || eventId <= 0) {
      return;
    }

    if (urlSelectedStudio) {
      const targetStudioId = Number(urlSelectedStudio);
      if (
        !Number.isNaN(targetStudioId) &&
        targetStudioId > 0 &&
        selectedStudioId !== targetStudioId
      ) {
        // Studio from the link hasn't been applied yet — wait for it.
        return;
      }
    } else if (selectedStudioId <= 0) {
      return;
    }

    navigate(constructPlannerEventHref(eventId), { replace: true });
  }, [urlEventId, urlSelectedStudio, selectedStudioId, navigate]);

  const onAddClick = useCallback(() => {
    const date = selectedValue as Date;

    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    navigate(constructAddEventHref(day, month, year));
  }, [selectedValue, navigate]);

  useAddAdditionalLayoutButton({
    iconType: IconType.PlusIcon,
    dependencies: [selectedValue],
    buttonOnClick: onAddClick,
  });

  const handleDateChange = (value: Value) => {
    if (value instanceof Date) {
      // Handle single date selection
      setSelectedValue(value);
      setCalendarViewDate(value);
    } else if (Array.isArray(value)) {
      // Handle date range selection (if applicable)
      // For example, you might want to set the start date of the range
      setSelectedValue(value[0] as Date);
      setCalendarViewDate(value[0] as Date);
    } else if (value === null) {
      // Handle null case (if applicable)
      // For example, you might want to reset the selected date
      setSelectedValue(new Date());
      setCalendarViewDate(new Date());
    }
  };

  const handleActiveStartDateChange = ({
    activeStartDate,
  }: {
    activeStartDate: Date | null;
  }) => {
    if (activeStartDate) {
      setCalendarViewDate(activeStartDate);
    }
  };

  const {
    data: newEvents,
    isLoading,
    dataUpdatedAt,
  } = useApiQuery<IPlannerDayModel[]>({
    queryKey: [
      endpointsMetadata.planner.getAll,
      selectedStudioId,
      calendarViewDate?.getFullYear(),
      calendarViewDate?.getMonth() + 1,
    ],
    endpoint: selectedValue
      ? `${
          endpointsMetadata.planner.getAll
        }?studioId=${selectedStudioId}&year=${calendarViewDate.getFullYear()}&month=${
          calendarViewDate.getMonth() + 1
        }`
      : `${endpointsMetadata.planner.getAll}?studioId=${selectedStudioId}`,
    enabled: selectedStudioId > 0 && !!selectedValue && !!calendarViewDate,
    globalIsLoading: false,
    disableErrorToast: false,
    retry: false,
  });

  const { data: studioEmployees } = useApiQuery<ISelectFieldModel[]>({
    queryKey: [
      endpointsMetadata.studio.getStudioEmployeesSimplified,
      selectedStudioId,
    ],
    endpoint: endpointsMetadata.studio.getStudioEmployeesSimplified,
    enabled: selectedStudioId > 0,
    disableErrorToast: false,
    retry: false,
    globalIsLoading: false,
  });

  const { data: studioHalls } = useApiQuery<ISelectFieldModel[]>({
    queryKey: [
      endpointsMetadata.studioHallTypeNomenclature.getAll,
      selectedStudioId,
    ],
    endpoint: endpointsMetadata.studioHallTypeNomenclature.getAll,
    enabled: selectedStudioId > 0 && tenantUsesMoreThanOneHall,
    disableErrorToast: false,
    retry: false,
    globalIsLoading: false,
  });

  const openFiltersSidebar = useCallback(() => {
    setIsFiltersSidebarOpen(true);
  }, []);

  const closeFiltersSidebar = useCallback(() => {
    setIsFiltersSidebarOpen(false);
  }, []);

  const openBulkAssignInstructorDialog = useCallback(() => {
    setIsBulkAssignInstructorDialogOpen(true);
  }, []);

  const closeBulkAssignInstructorDialog = useCallback(() => {
    setIsBulkAssignInstructorDialogOpen(false);
  }, []);

  const openBulkDeleteEventsDialog = useCallback(() => {
    setIsBulkDeleteEventsDialogOpen(true);
  }, []);

  const closeBulkDeleteEventsDialog = useCallback(() => {
    setIsBulkDeleteEventsDialogOpen(false);
  }, []);

  // Assigning instructors in bulk is a manager-and-up action (Tenant and GlobalAdmin
  // included); an instructor must not be able to hand themselves classes.
  const currentUserRole = getCurrentUserRole(currentUser);
  const canBulkAssignInstructor =
    currentUserRole !== undefined &&
    currentUserRole >= UserRole.Employee_Manager;
  // Deleting many events at once is just as much a manager tool.
  const canBulkDeleteEvents = canBulkAssignInstructor;

  useEffect(() => {
    const handleCacheClear = () => setEventsCache(new Map());
    window.addEventListener("clearAllCache", handleCacheClear);
    return () => window.removeEventListener("clearAllCache", handleCacheClear);
  }, []);

  const { onEventClick } = usePlannerEventClick();

  const allEvents = useMemo(
    () => getAllEventsFromCache(eventsCache),
    [eventsCache]
  );

  const eventsForCurrentDate = useMemo(() => {
    const selectedDate = selectedValue as Date;
    const selectedMonthKey = createCacheKey(
      selectedDate.getFullYear(),
      selectedDate.getMonth() + 1
    );

    // Prefer the selected date's own month cache to avoid stale overflow data
    // from adjacent months (e.g. May 1st appearing in April's response with fewer events)
    const ownMonthEvents = eventsCache.get(selectedMonthKey);
    const searchSource = ownMonthEvents ?? allEvents;

    const events = searchSource?.find(
      (x) =>
        dateUtility.formatDate(x.date) ===
        dateUtility.formatDate(selectedDate)
    )?.events;

    return filterEventsByFilters(events, filters);
  }, [selectedValue, allEvents, eventsCache, filters]);

  const updateFilters = (newFilters: Partial<IPlannerFiltersState>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const clearAllFilters = () => {
    setFilters(createDefaultFiltersState());
  };

  const hasActiveHallFilter =
    tenantUsesMoreThanOneHall && !isAllHallsSelected(filters.selectedHallIds);
  const activeHallCount = hasActiveHallFilter
    ? filters.selectedHallIds.filter((id) => id !== ALL_HALLS_OPTION_ID).length
    : 0;

  const hasActiveFilters =
    filters.selectedInstructors.length > 0 ||
    filters.selectedEventTypes.length > 0 ||
    filters.showDeletedEvents ||
    hasActiveHallFilter;
  const totalActiveFilters =
    filters.selectedInstructors.length +
    filters.selectedEventTypes.length +
    (filters.showDeletedEvents ? 1 : 0) +
    activeHallCount;

  const tileContent = useMemo(
    () => createTileContentFunction(allEvents, filters),
    [allEvents, filters]
  );

  useEffect(() => {
    if (!newEvents || !dataUpdatedAt) return;

    if (shouldUpdateCache(dataUpdatedAt, cacheTimestamps, currentCacheKey)) {
      setEventsCache((prev) =>
        updateEventsCache(prev, currentCacheKey, newEvents)
      );
      setCacheTimestamps((prev) =>
        updateCacheTimestamps(prev, currentCacheKey, dataUpdatedAt)
      );
    }
  }, [newEvents, currentCacheKey, dataUpdatedAt, cacheTimestamps]);

  useEffect(() => {
    saveFiltersToStorage(filters);
  }, [filters]);

  useEffect(() => {
    const updatedFilters = updateInstructorLabels(filters, studioEmployees);
    if (updatedFilters) {
      setFilters(updatedFilters);
    }
  }, [studioEmployees]);

  useEffect(() => {
    if (selectedValue) {
      const year = selectedValue.getFullYear();
      const month = String(selectedValue.getMonth() + 1).padStart(2, "0");
      const day = String(selectedValue.getDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;

      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("date", formattedDate);
      // replace so browsing dates in the calendar does not pile up history
      // entries (otherwise "back" from a class would step through every date).
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [selectedValue, setSearchParams]);

  const handleDeleteEvent = useCallback(
    (item: IPlannerEventModel) => {
      const hasReservations =
        (item.takenSpaces ?? 0) > 0 || (item.waitingCount ?? 0) > 0;
      const message = hasReservations
        ? "Всички резервации за този клас ще бъдат маркирани като изтрити. Наистина ли искате да го изтриете?"
        : "Сигурни ли сте, че искате да изтриете това събитие?";

      showCustomConfirmDialog(
        message,
        () =>
          mutate({
            endpoint: `${endpointsMetadata.planner.deleteEvent}?itemId=${item.id}`,
            method: "delete",
            invalidateKeys: [[endpointsMetadata.planner.getAll]],
          }),
        () => {},
        "warning",
        "Да",
        "Не"
      );
    },
    [mutate]
  );

  return {
    isLoading: isLoading && !eventsForCurrentDate,
    eventsForCurrentDate,
    selectedValue,
    studioEmployees,
    studioHalls,
    tenantUsesMoreThanOneHall,
    onEventClick,
    handleActiveStartDateChange,
    tileContent,
    handleDateChange,
    filters,
    updateFilters,
    clearAllFilters,
    hasActiveFilters,
    totalActiveFilters,
    // Filters sidebar
    isFiltersSidebarOpen,
    openFiltersSidebar,
    closeFiltersSidebar,
    // Bulk instructor assignment dialog
    canBulkAssignInstructor,
    isBulkAssignInstructorDialogOpen,
    openBulkAssignInstructorDialog,
    closeBulkAssignInstructorDialog,
    // Bulk delete dialog
    canBulkDeleteEvents,
    isBulkDeleteEventsDialogOpen,
    openBulkDeleteEventsDialog,
    closeBulkDeleteEventsDialog,
    handleDeleteEvent,
    // Panel expand (stacked layouts only)
    canExpandPanel: panelExpand.canExpand,
    isPanelExpanded: panelExpand.isExpanded,
    isPanelAnimating: panelExpand.isAnimating,
    togglePanelExpanded: panelExpand.toggleExpanded,
    panelRef: panelExpand.panelRef,
    plannerAreaRef: panelExpand.areaRef,
    panelStyle: panelExpand.panelStyle,
    // Hand-over note for the selected day
    handover,
    // Search dialog (from useReservationSearch hook)
    ...reservationSearch,
  };
};
